/* iter-44 — Demo first-load tour modal.
 *
 * Pops once on the first workspace load for a demo visitor (gated by
 * localStorage so a refresh doesn't re-trigger it). Sets expectations
 * for what's free vs locked so the user doesn't hit a wall blind.
 */
import React from 'react';
import { Check, Lock, Sparkles, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth_context';

const STORAGE_KEY = 'pw_demo_tour_shown';

const FREE = [
  'Browse chapters, modes, variables, and the editor',
  'Read & edit characters, traits and the arc visualizer',
  'Save your project locally and export to plain text',
  'Explore every panel, dialog and shortcut',
];

const LOCKED = [
  'Real AI chat / story generation / translations',
  'Token top-ups & subscription checkout',
  'Paid editing, publishing & custom cover art',
  'Bookstore purchases & royalty payouts',
];

export default function DemoTourModal() {
  const { user, login } = useAuth();
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!user?.is_demo) return;
    try {
      if (window.localStorage.getItem(STORAGE_KEY) === '1') return;
    } catch { /* localStorage blocked — show once per page load */ }
    const t = window.setTimeout(() => setOpen(true), 1200);
    return () => window.clearTimeout(t);
  }, [user?.is_demo]);

  const dismiss = () => {
    try { window.localStorage.setItem(STORAGE_KEY, '1'); }
    catch { /* ignore */ }
    setOpen(false);
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
      data-testid="demo-tour-modal"
    >
      <div className="w-full max-w-lg overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
        <div className="relative border-b border-border bg-gradient-to-br from-primary/15 via-card to-card px-6 py-5">
          <button
            onClick={dismiss}
            className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
            data-testid="demo-tour-close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2 text-primary">
            <Sparkles className="h-4 w-4" />
            <span className="text-[10.5px] uppercase tracking-[0.18em]">Welcome to the demo</span>
          </div>
          <h2 className="mt-1 text-[19px] font-semibold tracking-tight">
            What you can try — and what's saved for the real thing
          </h2>
          <p className="mt-1 text-[12.5px] text-muted-foreground leading-relaxed">
            Click around freely. Anything 🔒 is locked in demo mode — sign up free in 10 seconds to unlock it.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 px-6 py-5 sm:grid-cols-2">
          <div>
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-success">
              <Check className="h-3.5 w-3.5" /> Free in demo
            </div>
            <ul className="space-y-1.5 text-[12.5px]" data-testid="demo-tour-free-list">
              {FREE.map((p) => (
                <li key={p} className="flex items-start gap-2 leading-snug">
                  <Check className="mt-0.5 h-3 w-3 shrink-0 text-success" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
              <Lock className="h-3.5 w-3.5" /> Locked until upgrade
            </div>
            <ul className="space-y-1.5 text-[12.5px]" data-testid="demo-tour-locked-list">
              {LOCKED.map((p) => (
                <li key={p} className="flex items-start gap-2 leading-snug">
                  <Lock className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border bg-panel-header/40 px-6 py-3">
          <Button variant="ghost" size="sm" onClick={dismiss} data-testid="demo-tour-explore">
            Start exploring
          </Button>
          <Button size="sm" className="gap-1.5" onClick={login} data-testid="demo-tour-upgrade">
            Skip — start free trial <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
